/**
 * Transform nodes — get/set position, rotation, scale of game objects.
 */
import type { NodeBehaviour } from '@engine/redprint';
import { flowIn, flowOut, input, output } from '@engine/redprint';

export const GetPosition: NodeBehaviour = {
  id: 'transform.getPosition',
  name: 'Get Position',
  category: 'Transform',
  isPure: true,
  color: '#00d4ff',
  icon: 'MapPin',
  description: 'Gets the position of an entity.',
  keywords: ['position', 'location', 'coordinates', 'xy'],
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('pos', 'Position', 'vec2'),
    output('x', 'X', 'f32'),
    output('y', 'Y', 'f32'),
  ],
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    const x = ctx.bridge.getPositionX(eid);
    const y = ctx.bridge.getPositionY(eid);
    ctx.setOutput('pos', { x, y });
    ctx.setOutput('x', x);
    ctx.setOutput('y', y);
  },
  compile(ctx) {
    const ent = ctx.resolveInput('entity');
    // Returns vec2, but individual outputs can be accessed via .x/.y
    return ctx.emit('bridge:getPosition', [ent], 'vec2');
  },
};

export const SetPosition: NodeBehaviour = {
  id: 'transform.setPosition',
  name: 'Set Position',
  category: 'Transform',
  color: '#00d4ff',
  icon: 'Move',
  description: 'Sets the position of an entity.',
  keywords: ['position', 'move', 'teleport', 'place'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
  ],
  outputs: [
    flowOut(),
  ],
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    const x = ctx.getInput<number>('x');
    const y = ctx.getInput<number>('y');
    ctx.bridge.setPosition(eid, x, y);
  },
  compile(ctx) {
    ctx.emit('bridge:setPosition', [ctx.resolveInput('entity'), ctx.resolveInput('x'), ctx.resolveInput('y')], 'entity', true);
  },
};

export const GetRotation: NodeBehaviour = {
  id: 'transform.getRotation',
  name: 'Get Rotation',
  category: 'Transform',
  isPure: true,
  color: '#00d4ff',
  icon: 'RotateCw',
  description: 'Gets the rotation angle (radians) of an entity.',
  keywords: ['angle', 'orientation'],
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('angle', 'Angle', 'f32'),
  ],
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('angle', ctx.bridge.getRotation(eid));
  },
  compile(ctx) {
    return ctx.emit('bridge:getRotation', [ctx.resolveInput('entity')], 'f32');
  },
};

export const SetRotation: NodeBehaviour = {
  id: 'transform.setRotation',
  name: 'Set Rotation',
  category: 'Transform',
  color: '#00d4ff',
  icon: 'RotateCw',
  description: 'Sets the rotation angle (radians) of an entity.',
  keywords: ['angle', 'rotate', 'orientation'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('angle', 'Angle', 'f32', 0),
  ],
  outputs: [
    flowOut(),
  ],
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    const angle = ctx.getInput<number>('angle');
    ctx.bridge.setRotation(eid, angle);
  },
  compile(ctx) {
    ctx.emit('bridge:setRotation', [ctx.resolveInput('entity'), ctx.resolveInput('angle')], 'f32', true);
  },
};

export const GetScale: NodeBehaviour = {
  id: 'transform.getScale',
  name: 'Get Scale',
  category: 'Transform',
  isPure: true,
  color: '#00d4ff',
  icon: 'Maximize2',
  description: 'Gets the scale of an entity.',
  keywords: ['size', 'zoom'],
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('scale', 'Scale', 'vec2'),
    output('x', 'Scale X', 'f32'),
    output('y', 'Scale Y', 'f32'),
  ],
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    const sx = ctx.bridge.getScaleX(eid);
    const sy = ctx.bridge.getScaleY(eid);
    ctx.setOutput('scale', { x: sx, y: sy });
    ctx.setOutput('x', sx);
    ctx.setOutput('y', sy);
  },
  compile(ctx) {
    return ctx.emit('bridge:getScale', [ctx.resolveInput('entity')], 'vec2');
  },
};

export const SetScale: NodeBehaviour = {
  id: 'transform.setScale',
  name: 'Set Scale',
  category: 'Transform',
  color: '#00d4ff',
  icon: 'Maximize2',
  description: 'Sets the scale of an entity.',
  keywords: ['size', 'zoom', 'resize'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('x', 'Scale X', 'f32', 1),
    input('y', 'Scale Y', 'f32', 1),
  ],
  outputs: [
    flowOut(),
  ],
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.setScale(eid, ctx.getInput<number>('x'), ctx.getInput<number>('y'));
  },
  compile(ctx) {
    ctx.emit('bridge:setScale', [ctx.resolveInput('entity'), ctx.resolveInput('x'), ctx.resolveInput('y')], 'void', true);
    
  },
};

export const Translate: NodeBehaviour = {
  id: 'transform.translate',
  name: 'Translate',
  category: 'Transform',
  color: '#00d4ff',
  icon: 'ArrowRight',
  description: 'Moves an entity by a delta amount.',
  keywords: ['move', 'shift', 'offset', 'velocity'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('dx', 'Delta X', 'f32', 0),
    input('dy', 'Delta Y', 'f32', 0),
  ],
  outputs: [
    flowOut(),
  ],
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    const curX = ctx.bridge.getPositionX(eid);
    const curY = ctx.bridge.getPositionY(eid);
    ctx.bridge.setPosition(eid, curX + ctx.getInput<number>('dx'), curY + ctx.getInput<number>('dy'));
  },
  compile(ctx) {
    ctx.emit('bridge:translate', [ctx.resolveInput('entity'), ctx.resolveInput('dx'), ctx.resolveInput('dy')], 'void', true);
    
  },
};

export const LookAt: NodeBehaviour = {
  id: 'transform.lookAt',
  name: 'Look At',
  category: 'Transform',
  color: '#00d4ff',
  icon: 'Eye',
  description: 'Rotates entity to face a target position.',
  keywords: ['aim', 'face', 'direction'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('tx', 'Target X', 'f32', 0),
    input('ty', 'Target Y', 'f32', 0),
  ],
  outputs: [
    flowOut(),
  ],
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    const px = ctx.bridge.getPositionX(eid);
    const py = ctx.bridge.getPositionY(eid);
    const angle = Math.atan2(ctx.getInput<number>('ty') - py, ctx.getInput<number>('tx') - px);
    ctx.bridge.setRotation(eid, angle);
  },
  compile(ctx) {
    ctx.emit('bridge:lookAt', [ctx.resolveInput('entity'), ctx.resolveInput('tx'), ctx.resolveInput('ty')], 'void', true);
    
  },
};

// ============================================================================
// WORLD / LOCAL TRANSFORM
// ============================================================================

export const GetWorldPosition: NodeBehaviour = {
  id: 'transform.getWorldPosition',
  name: 'Get World Position',
  category: 'Transform',
  isPure: true,
  color: '#00d4ff',
  icon: 'Globe',
  description: 'Gets the world (global) position of an entity.',
  keywords: ['global', 'absolute'],
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('pos', 'Position', 'vec2'),
    output('x', 'X', 'f32'),
    output('y', 'Y', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getWorldPosition', [ctx.resolveInput('entity')], 'vec2');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    const r = ctx.bridge.call('bridge:getWorldPosition', eid) as { x: number; y: number } | null;
    ctx.setOutput('pos', r);
    ctx.setOutput('x', r?.x ?? 0);
    ctx.setOutput('y', r?.y ?? 0);
  },
};

export const SetWorldPosition: NodeBehaviour = {
  id: 'transform.setWorldPosition',
  name: 'Set World Position',
  category: 'Transform',
  color: '#00d4ff',
  icon: 'Globe',
  description: 'Sets the world (global) position of an entity.',
  keywords: ['global', 'absolute'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setWorldPosition', [ctx.resolveInput('entity'), ctx.resolveInput('x'), ctx.resolveInput('y')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setWorldPosition', eid, ctx.getInput<number>('x'), ctx.getInput<number>('y'));
  },
};

export const GetWorldRotation: NodeBehaviour = {
  id: 'transform.getWorldRotation',
  name: 'Get World Rotation',
  category: 'Transform',
  isPure: true,
  color: '#00d4ff',
  icon: 'Globe',
  description: 'Gets the world (global) rotation of an entity.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('angle', 'Angle', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getWorldRotation', [ctx.resolveInput('entity')], 'f32');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('angle', ctx.bridge.call('bridge:getWorldRotation', eid) as number ?? 0);
  },
};

export const GetLocalPosition: NodeBehaviour = {
  id: 'transform.getLocalPosition',
  name: 'Get Local Position',
  category: 'Transform',
  isPure: true,
  color: '#00d4ff',
  icon: 'Home',
  description: 'Gets the local position relative to parent.',
  keywords: ['relative', 'parent'],
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('pos', 'Position', 'vec2'),
    output('x', 'X', 'f32'),
    output('y', 'Y', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getLocalPosition', [ctx.resolveInput('entity')], 'vec2');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    const r = ctx.bridge.call('bridge:getLocalPosition', eid) as { x: number; y: number } | null;
    ctx.setOutput('pos', r);
    ctx.setOutput('x', r?.x ?? 0);
    ctx.setOutput('y', r?.y ?? 0);
  },
};

export const SetLocalPosition: NodeBehaviour = {
  id: 'transform.setLocalPosition',
  name: 'Set Local Position',
  category: 'Transform',
  color: '#00d4ff',
  icon: 'Home',
  description: 'Sets the local position relative to parent.',
  keywords: ['relative'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setLocalPosition', [ctx.resolveInput('entity'), ctx.resolveInput('x'), ctx.resolveInput('y')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setLocalPosition', eid, ctx.getInput<number>('x'), ctx.getInput<number>('y'));
  },
};

// ============================================================================
// ROTATION HELPERS
// ============================================================================

export const Rotate: NodeBehaviour = {
  id: 'transform.rotate',
  name: 'Rotate',
  category: 'Transform',
  color: '#00d4ff',
  icon: 'RotateCw',
  description: 'Rotates an entity by a delta angle.',
  keywords: ['turn', 'spin'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('angle', 'Delta Angle', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:rotate', [ctx.resolveInput('entity'), ctx.resolveInput('angle')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:rotate', eid, ctx.getInput<number>('angle'));
  },
};

export const SetUniformScale: NodeBehaviour = {
  id: 'transform.setUniformScale',
  name: 'Set Uniform Scale',
  category: 'Transform',
  color: '#00d4ff',
  icon: 'Maximize2',
  description: 'Sets the scale uniformly on both axes.',
  keywords: ['size', 'zoom'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('scale', 'Scale', 'f32', 1),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setUniformScale', [ctx.resolveInput('entity'), ctx.resolveInput('scale')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setUniformScale', eid, ctx.getInput<number>('scale'));
  },
};

// ============================================================================
// UTILITY CALCULATIONS
// ============================================================================

export const GetDistance: NodeBehaviour = {
  id: 'transform.getDistance',
  name: 'Get Distance',
  category: 'Transform',
  isPure: true,
  color: '#00d4ff',
  icon: 'Ruler',
  description: 'Gets the distance between two entities.',
  keywords: ['length', 'between'],
  inputs: [
    input('entityA', 'Entity A', 'entity', 0),
    input('entityB', 'Entity B', 'entity', 0),
  ],
  outputs: [
    output('distance', 'Distance', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getDistanceBetween', [ctx.resolveInput('entityA'), ctx.resolveInput('entityB')], 'f32');
  },
  execute(ctx) {
    const a = ctx.getInput<number>('entityA') || ctx.entityId;
    const b = ctx.getInput<number>('entityB') || ctx.entityId;
    ctx.setOutput('distance', ctx.bridge.call('bridge:getDistanceBetween', a, b) as number ?? 0);
  },
};

export const GetDistanceToPoint: NodeBehaviour = {
  id: 'transform.getDistancePoint',
  name: 'Get Distance To Point',
  category: 'Transform',
  isPure: true,
  color: '#00d4ff',
  icon: 'Ruler',
  description: 'Gets the distance from entity to a point.',
  keywords: ['length'],
  inputs: [
    input('entity', 'Entity', 'entity', 0),
    input('x', 'Target X', 'f32', 0),
    input('y', 'Target Y', 'f32', 0),
  ],
  outputs: [
    output('distance', 'Distance', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getDistanceToPoint', [ctx.resolveInput('entity'), ctx.resolveInput('x'), ctx.resolveInput('y')], 'f32');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('distance', ctx.bridge.call('bridge:getDistanceToPoint', eid, ctx.getInput<number>('x'), ctx.getInput<number>('y')) as number ?? 0);
  },
};

export const GetDirectionTo: NodeBehaviour = {
  id: 'transform.getDirectionTo',
  name: 'Get Direction To',
  category: 'Transform',
  isPure: true,
  color: '#00d4ff',
  icon: 'Navigation',
  description: 'Gets normalized direction from one entity to another.',
  keywords: ['vector', 'towards'],
  inputs: [
    input('from', 'From Entity', 'entity', 0),
    input('to', 'To Entity', 'entity', 0),
  ],
  outputs: [
    output('direction', 'Direction', 'vec2'),
    output('angle', 'Angle', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getDirectionTo', [ctx.resolveInput('from'), ctx.resolveInput('to')], 'vec2');
  },
  execute(ctx) {
    const from = ctx.getInput<number>('from') || ctx.entityId;
    const to = ctx.getInput<number>('to') || ctx.entityId;
    const r = ctx.bridge.call('bridge:getDirectionTo', from, to) as { x: number; y: number; angle?: number } | null;
    ctx.setOutput('direction', r);
    ctx.setOutput('angle', r?.angle ?? Math.atan2(r?.y ?? 0, r?.x ?? 0));
  },
};

export const GetDirectionToPoint: NodeBehaviour = {
  id: 'transform.getDirectionToPoint',
  name: 'Get Direction To Point',
  category: 'Transform',
  isPure: true,
  color: '#00d4ff',
  icon: 'Navigation',
  description: 'Gets normalized direction from entity to a point.',
  keywords: ['vector', 'towards'],
  inputs: [
    input('entity', 'Entity', 'entity', 0),
    input('x', 'Target X', 'f32', 0),
    input('y', 'Target Y', 'f32', 0),
  ],
  outputs: [
    output('direction', 'Direction', 'vec2'),
    output('angle', 'Angle', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getDirectionToPoint', [ctx.resolveInput('entity'), ctx.resolveInput('x'), ctx.resolveInput('y')], 'vec2');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    const r = ctx.bridge.call('bridge:getDirectionToPoint', eid, ctx.getInput<number>('x'), ctx.getInput<number>('y')) as { x: number; y: number; angle?: number } | null;
    ctx.setOutput('direction', r);
    ctx.setOutput('angle', r?.angle ?? Math.atan2(r?.y ?? 0, r?.x ?? 0));
  },
};

export const GetForwardVector: NodeBehaviour = {
  id: 'transform.getForward',
  name: 'Get Forward Vector',
  category: 'Transform',
  isPure: true,
  color: '#00d4ff',
  icon: 'ArrowUp',
  description: 'Gets the forward direction based on rotation.',
  keywords: ['facing', 'direction'],
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('forward', 'Forward', 'vec2'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getForwardVector', [ctx.resolveInput('entity')], 'vec2');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('forward', ctx.bridge.call('bridge:getForwardVector', eid) as { x: number; y: number } | null ?? { x: 1, y: 0 });
  },
};

export const GetRightVector: NodeBehaviour = {
  id: 'transform.getRight',
  name: 'Get Right Vector',
  category: 'Transform',
  isPure: true,
  color: '#00d4ff',
  icon: 'ArrowRight',
  description: 'Gets the right direction based on rotation.',
  keywords: ['perpendicular', 'side'],
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('right', 'Right', 'vec2'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getRightVector', [ctx.resolveInput('entity')], 'vec2');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('right', ctx.bridge.call('bridge:getRightVector', eid) as { x: number; y: number } | null ?? { x: 0, y: -1 });
  },
};

export const TransformPoint: NodeBehaviour = {
  id: 'transform.transformPoint',
  name: 'Transform Point',
  category: 'Transform',
  isPure: true,
  color: '#00d4ff',
  icon: 'Move',
  description: 'Transforms a point from local to world space.',
  keywords: ['convert', 'space'],
  inputs: [
    input('entity', 'Entity', 'entity', 0),
    input('x', 'Local X', 'f32', 0),
    input('y', 'Local Y', 'f32', 0),
  ],
  outputs: [
    output('world', 'World Position', 'vec2'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:transformPoint', [ctx.resolveInput('entity'), ctx.resolveInput('x'), ctx.resolveInput('y')], 'vec2');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('world', ctx.bridge.call('bridge:transformPoint', eid, ctx.getInput<number>('x'), ctx.getInput<number>('y')) as { x: number; y: number } | null ?? { x: 0, y: 0 });
  },
};

export const InverseTransformPoint: NodeBehaviour = {
  id: 'transform.inverseTransformPoint',
  name: 'Inverse Transform Point',
  category: 'Transform',
  isPure: true,
  color: '#00d4ff',
  icon: 'Move',
  description: 'Transforms a point from world to local space.',
  keywords: ['convert', 'space'],
  inputs: [
    input('entity', 'Entity', 'entity', 0),
    input('x', 'World X', 'f32', 0),
    input('y', 'World Y', 'f32', 0),
  ],
  outputs: [
    output('local', 'Local Position', 'vec2'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:inverseTransformPoint', [ctx.resolveInput('entity'), ctx.resolveInput('x'), ctx.resolveInput('y')], 'vec2');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('local', ctx.bridge.call('bridge:inverseTransformPoint', eid, ctx.getInput<number>('x'), ctx.getInput<number>('y')) as { x: number; y: number } | null ?? { x: 0, y: 0 });
  },
};

export const TRANSFORM_NODES: readonly NodeBehaviour[] = [
  // Basic
  GetPosition, SetPosition, GetRotation, SetRotation,
  GetScale, SetScale, Translate, LookAt,
  // World/Local
  GetWorldPosition, SetWorldPosition, GetWorldRotation,
  GetLocalPosition, SetLocalPosition,
  // Rotation helpers
  Rotate, SetUniformScale,
  // Utility calculations
  GetDistance, GetDistanceToPoint, GetDirectionTo, GetDirectionToPoint,
  GetForwardVector, GetRightVector, TransformPoint, InverseTransformPoint,
];
